import React, { useState, useEffect, useRef } from 'react';
import { useAbdm } from '../context/AbdmContext';
import { handleRequestOTP } from '../../utils/api';
import { creationFlows, verificationFlows, utilityFlows, FlowOption, AuthConfig, API_BASE } from '../../utils/types';
import { getFlowDescription, getValueLabel } from '../../utils/helpers';

type TabKey = 'creation' | 'verification' | 'utility';

export const AuthTypeSelector: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { activeTab, authConfig, authData } = state;
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [selectedFlowId, setSelectedFlowId] = useState<string>('');
    const [showAadhaar, setShowAadhaar] = useState(false);
    const [nonKycChecking, setNonKycChecking] = useState(false);
    const [nonKycResult, setNonKycResult] = useState<boolean | null>(null);
    const [validationMsg, setValidationMsg] = useState('');
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const tab = (activeTab || 'creation') as TabKey;
    const currentFlows = tab === 'creation' ? creationFlows
        : tab === 'verification' ? verificationFlows
        : utilityFlows;

    // Flows that navigate directly to their own step (no OTP request from this screen)
    const directNavFlows = new Set([
        'face-auth-enroll', 'face-auth-login', 'biometric-enroll',
        'find-abha', 'find-by-mobile', 'enrollment',
    ]);

    // Set initial selected flow when tab changes
    useEffect(() => {
        if (currentFlows.length > 0) {
            const firstFlow = currentFlows[0];
            setSelectedFlowId(firstFlow.id);
            dispatch({
                type: 'SET_AUTH_CONFIG', payload: {
                    ...authConfig,
                    type: firstFlow.type as AuthConfig['type'],
                    method: firstFlow.method as AuthConfig['method'],
                    value: '',
                    aadhaarMobileInput: '',
                    nonKyc: false,
                    maskedMobile: undefined,
                    ...(firstFlow.preset || {})
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const onSelectFlow = (flow: FlowOption) => {
        setSelectedFlowId(flow.id);
        setOtpError('');
        setValidationMsg('');
        setNonKycResult(null);
        dispatch({
            type: 'SET_AUTH_CONFIG', payload: {
                ...authConfig,
                type: flow.type as AuthConfig['type'],
                method: flow.method as AuthConfig['method'],
                value: '',
                aadhaarMobileInput: '',
                nonKyc: false,
                maskedMobile: undefined,
                ...(flow.preset || {})
            }
        });
    };

    // Debounced non-KYC check for ABHA address
    useEffect(() => {
        if (authConfig.type !== 'abha-address' || !authConfig.value || authConfig.value.length < 4) {
            setNonKycResult(null);
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setNonKycChecking(true);
            try {
                const resp = await fetch(`${API_BASE}/api/abdm/phr/web/login/abha/search`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authData?.token}` },
                    body: JSON.stringify({ abhaAddress: authConfig.value })
                });
                const data = await resp.json();
                const isNonKyc = data.kycStatus === 'PENDING' || data.kycVerified === false || data.nonKyc === true;
                setNonKycResult(isNonKyc);
                dispatch({ type: 'SET_AUTH_CONFIG', payload: { ...authConfig, nonKyc: isNonKyc } });
            } catch {
                setNonKycResult(null);
            } finally {
                setNonKycChecking(false);
            }
        }, 800);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authConfig.value, authConfig.type]);

    // Input validation
    const validateInput = (value: string): string => {
        const { type, method } = authConfig;
        if (!value) return '';
        // Check type-specific validation first (takes priority over method)
        if (type === 'abha-number') {
            const digits = value.replace(/[-\s]/g, '');
            if (digits.length > 0 && digits.length < 14) return 'ABHA number must be 14 digits';
            return '';
        }
        if (type === 'abha-address') {
            return ''; // ABHA address has no strict digit validation
        }
        if (isAadhaarInput) {
            if (value.length > 0 && value.length < 12) return 'Aadhaar number must be 12 digits';
            if (value.length === 12 && !/^\d{12}$/.test(value)) return 'Aadhaar must contain only digits';
        }
        if (method === 'mobile') {
            if (value.length > 0 && value.length < 10) return 'Mobile number must be 10 digits';
            if (value.length === 10 && !/^\d{10}$/.test(value)) return 'Mobile must contain only digits';
        }
        return '';
    };

    const handleValueChange = (value: string) => {
        // ABHA address accepts alphanumeric + dots/underscores, don't strip
        const isNumericOnly = authConfig.type === 'abha-number' || isAadhaarInput ||
            (authConfig.method === 'mobile' && authConfig.type !== 'abha-address');
        const cleanValue = isNumericOnly ? value.replace(/\D/g, '') : value;
        dispatch({ type: 'SET_AUTH_CONFIG', payload: { ...authConfig, value: cleanValue } });
        setValidationMsg(validateInput(cleanValue));
    };

    // Route to the correct step
    const getDirectNavStep = (type: string): string => {
        switch (type) {
            case 'face-auth-enroll': return 'faceAuthEnroll';
            case 'face-auth-login': return 'faceAuthLogin';
            case 'biometric-enroll': return 'biometricEnroll';
            case 'find-abha': return 'findAbha';
            case 'find-by-mobile': return 'findAbhaByMobile';
            case 'enrollment': return 'aadhaarEnroll';
            case 'forgot-abha': return 'forgotAbha';
            default: return 'otpVerify';
        }
    };

    const isDirectNav = directNavFlows.has(authConfig.type);

    const onSubmit = async () => {
        // Direct navigation flows (no OTP needed)
        if (isDirectNav) {
            dispatch({ type: 'SET_STEP', payload: getDirectNavStep(authConfig.type) });
            return;
        }

        // Forgot ABHA flows go directly to their step
        if (authConfig.type === 'forgot-abha') {
            dispatch({ type: 'SET_STEP', payload: 'forgotAbha' });
            return;
        }

        // OTP-based flows
        setOtpLoading(true);
        setOtpError('');
        try {
            const result = await handleRequestOTP(authData!, authConfig);
            dispatch({ type: 'SET_TRANSACTION_ID', payload: result.txnId });
            if (result.maskedMobile) {
                dispatch({ type: 'SET_AUTH_CONFIG', payload: { ...authConfig, maskedMobile: result.maskedMobile } });
            }
            dispatch({ type: 'SET_STEP', payload: 'otpVerify' });
        } catch (error) {
            setOtpError((error as Error).message);
        } finally {
            setOtpLoading(false);
        }
    };

    // Only mask input as Aadhaar when the VALUE being entered is an Aadhaar number
    // (enrollment+aadhaar or aadhaar-login). For abha-number+aadhaar, the input is an ABHA number.
    const isAadhaarInput = (authConfig.type === 'enrollment' && authConfig.method === 'aadhaar') || authConfig.type === 'aadhaar-login';

    const getInputMaxLength = (): number | undefined => {
        if (authConfig.type === 'abha-number') return 17; // 14 digits + possible dashes
        if (authConfig.type === 'abha-address') return undefined; // ABHA address can vary in length
        if (isAadhaarInput) return 12;
        if (authConfig.method === 'mobile') return 10;
        return undefined;
    };

    const isSubmitDisabled = (): boolean => {
        if (otpLoading) return true;
        // Direct nav flows don't need value input
        if (isDirectNav) return false;
        if (authConfig.type === 'forgot-abha') {
            if (!authConfig.value.trim()) return true;
            return !!validateInput(authConfig.value);
        }
        if (!authConfig.value.trim()) return true;
        if (validateInput(authConfig.value)) return true;
        return false;
    };

    const getButtonLabel = (): string => {
        if (otpLoading) return '📡 Requesting OTP...';
        if (isDirectNav) {
            if (authConfig.type === 'face-auth-enroll') return '🤳 Start Face Auth Enrollment';
            if (authConfig.type === 'face-auth-login') return '🤳 Start Face Auth Login';
            if (authConfig.type === 'biometric-enroll') return '🔒 Start Biometric Enrollment';
            if (authConfig.type === 'find-abha') return '🔍 Search ABHA';
            if (authConfig.type === 'find-by-mobile') return '🔍 Find ABHA by Mobile';
            if (authConfig.type === 'enrollment') return '📝 Start ABHA Creation';
            return '▶ Start';
        }
        if (authConfig.type === 'forgot-abha') return '🔍 Retrieve ABHA';
        return `🔐 Start ${getFlowDescription(authConfig.type, authConfig.method)}`;
    };

    // Whether to show the input field
    const showInput = !isDirectNav;

    if (!authData) return null;

    return (
        <div className="step-container">
            <h2>Select ABHA Flow</h2>
            <div className="tabs">
                {(['creation', 'verification', 'utility'] as TabKey[]).map(t => (
                    <button
                        key={t}
                        className={`tab ${tab === t ? 'active' : ''}`}
                        onClick={() => {
                            setSelectedFlowId('');
                            setOtpError('');
                            setValidationMsg('');
                            dispatch({ type: 'SET_ACTIVE_TAB', payload: t });
                        }}
                    >
                        {t === 'creation' ? 'Creation' : t === 'verification' ? 'Verification' : 'Utility'}
                    </button>
                ))}
            </div>

            <div className="flow-options" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                {currentFlows.map((flow) => (
                    <div
                        key={flow.id}
                        className={`flow-option ${selectedFlowId === flow.id ? 'selected' : ''}`}
                        onClick={() => onSelectFlow(flow)}
                        style={{ flexDirection: 'row', textAlign: 'left', alignItems: 'center', padding: '10px 12px', gap: '10px', margin: 0 }}
                    >
                        <span className="flow-icon" style={{ fontSize: '1.3rem' }}>{flow.icon}</span>
                        <div className="flow-details">
                            <strong style={{ fontSize: '0.9rem' }}>{flow.title}</strong>
                            <span style={{ fontSize: '0.75rem' }}>{flow.desc}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Info message for direct-nav flows */}
            {isDirectNav && (
                <div style={{ background: '#e7f1ff', border: '1px solid #007bff', borderRadius: '8px', padding: '12px 14px', marginBottom: '12px', fontSize: '0.9em' }}>
                    {authConfig.type === 'face-auth-enroll' && <><strong>Face Auth Enrollment:</strong> You will enter Aadhaar & mobile on the next screen, then scan a QR code with your ABHA App.</>}
                    {authConfig.type === 'face-auth-login' && <><strong>Face Auth Login:</strong> You will search by mobile number, select your account, then verify via QR face scan.</>}
                    {authConfig.type === 'biometric-enroll' && <><strong>Biometric Enrollment:</strong> You will use an RD device (fingerprint/iris) on the next screen.</>}
                    {authConfig.type === 'find-abha' && <><strong>Find ABHA:</strong> Search for an existing ABHA by number or address.</>}
                    {authConfig.type === 'find-by-mobile' && <><strong>Find by Mobile:</strong> Search all ABHA IDs linked to a mobile number.</>}
                    {authConfig.type === 'enrollment' && <><strong>Create ABHA:</strong> Enter Aadhaar, provide consent, verify OTP, and create your ABHA address.</>}
                </div>
            )}

            {/* Input field (hidden for direct-nav flows) */}
            {showInput && (
                <div className="form-group">
                    <label htmlFor="value">{getValueLabel(authConfig.type, authConfig.method)}</label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <input
                            id="value"
                            type={isAadhaarInput && !showAadhaar ? 'password' : 'text'}
                            value={authConfig.value}
                            onChange={(e) => handleValueChange(e.target.value)}
                            placeholder={`Enter ${getValueLabel(authConfig.type, authConfig.method)}`}
                            maxLength={getInputMaxLength()}
                            style={{ flex: 1 }}
                            onKeyDown={e => e.key === 'Enter' && !isSubmitDisabled() && onSubmit()}
                        />
                        {isAadhaarInput && (
                            <button
                                type="button"
                                onClick={() => setShowAadhaar(!showAadhaar)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2em', marginLeft: '8px', padding: '4px' }}
                                title={showAadhaar ? 'Hide Aadhaar' : 'Show Aadhaar'}
                            >
                                {showAadhaar ? '🙈' : '👁️'}
                            </button>
                        )}
                    </div>
                    {validationMsg && <small style={{ color: '#dc3545', marginTop: '4px', display: 'block' }}>{validationMsg}</small>}
                    {nonKycChecking && <small style={{ color: '#6c757d', marginTop: '4px', display: 'block' }}>Checking account status...</small>}
                </div>
            )}

            {/* Non-KYC warning */}
            {nonKycResult === true && authConfig.type === 'abha-address' && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '0.9em' }}>
                    <strong>Non-KYC Account:</strong> This ABHA address is not KYC-verified. Only Mobile OTP will work.
                </div>
            )}

            <button onClick={onSubmit} disabled={isSubmitDisabled()} className="btn-primary">
                {getButtonLabel()}
            </button>

            {otpError && <div className="error-message">Error: {otpError}</div>}
        </div>
    );
};
